/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo, useState } from 'react';
import Model, { helper } from '@tripian/model';
import Price from '../Price/Price';
import ImgLazy from '../base/ImgLazy/ImgLazy';
import StepTimes from '../StepTimes/StepTimes';
import CustomPopover from '../base/CustomPopover/CustomPopover';
import Info2 from '../base/Svg/Icons/Info2';
import { Dots, Replace, X, X2 } from '../base/Svg/Icons';
import emptyImageData from '../../constant/emptyImage';
import StarIcon from '../base/Svg/Icons/Star';
import TourTicketLazyLoading from '../TourTicketLazyLoading/TourTicketLazyLoading';
import classes from './StepCard.scss';
import luggageImageData from '../../constant/luggageImage';

interface IStepCard {
  step: Model.Step;
  clicked: (step: Model.Step) => void;
  alternativePois: Model.Poi[];
  alternativeReplace: (alternativePoi: Model.Poi) => void;
  showAlternativesChange: (stepId: number, show: boolean) => void;
  showAlternatives?: boolean;
  timesClicked: (from: string, to: string) => void;
  userReactionRemoveStep: () => void;
  TOUR_PROVIDER_IDS: Model.PROVIDER_ID[];
  TICKET_PROVIDER_IDS: Model.PROVIDER_ID[];
  RESTAURANT_RESERVATION_PROVIDER_IDS: Model.PROVIDER_ID[];
  hideReservationIcon: boolean;
  hideTourTicketIcons: boolean;
  // hideOfferIcon: boolean;
  hideStepsTime?: boolean;
  isWidget?: boolean;
  hideFeatures?: boolean;
  hideCuisine?: boolean;
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  tourTicketProductsLoading: boolean;
  onBookClick?: (step: Model.Step) => void;
  t: (value: Model.TranslationKey) => string;
}

const StepCard: React.FC<IStepCard> = ({
  step,
  clicked,
  alternativePois,
  TOUR_PROVIDER_IDS,
  TICKET_PROVIDER_IDS,
  RESTAURANT_RESERVATION_PROVIDER_IDS,
  hideReservationIcon,
  hideTourTicketIcons,
  // hideOfferIcon,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  alternativeReplace,
  timesClicked,
  userReactionRemoveStep,
  hideStepsTime = false,
  hideCuisine = false,
  isWidget = false,
  tourTicketProductsLoading,
  onBookClick,
  t,
}) => {
  const [showStepTimes, setShowStepTimes] = useState<boolean>(false);
  const [showMobileButtons, setShowMobileButtons] = useState<boolean>(false);
  // eslint-disable-next-line no-nested-ternary
  let placeImg = step.poi.image === null ? emptyImageData : step.stepType === 'activity' ? step.poi.image.url : helper.imgUrl(`${step.poi.image.url}`, 256, 256);
  if (step.stepType === 'luggage' && step.poi.image === null) {
    placeImg = luggageImageData;
  }
  const categoryId = step.poi.category?.[0]?.id;
  const attraction = categoryId ? helper.getCategoryGroup(categoryId) === Model.POI_CATEGORY_GROUP.ATTRACTION : false;
  const notCategorized = categoryId ? helper.getCategoryGroup(categoryId) === Model.POI_CATEGORY_GROUP.NOTCATEGORIZED : true;
  const cuisinesArray = step.poi.cuisines?.split(', ') || [];
  const uniqueCuisines = helper.removeDuplicateValues(cuisinesArray, (s1, s2) => s1 === s2);
  const stepsTime = useMemo(() => {
    if (step.times.from && step.times.to) {
      return (
        <CustomPopover
          show={showStepTimes}
          content={
            <StepTimes
              key={`step-times-${step.id}`}
              times={{
                from: step.times.from,
                to: step.times.to,
              }}
              timesClicked={(from?: string, to?: string) => {
                if (from && to) timesClicked(from, to);
                setShowStepTimes(false);
              }}
              buttonText={t('trips.myTrips.itinerary.step.addToItinerary.submit.apply')}
              duration={step.poi.duration}
              onClose={() => setShowStepTimes(false)}
              t={t}
            />
          }
          backdropClick={(e) => {
            e.stopPropagation();
            if (showStepTimes) setShowStepTimes(false);
          }}
          positions={['top']}
        >
          <div
            role="button"
            key={Math.floor(Math.random() * 100)}
            tabIndex={0}
            onKeyPress={() => {}}
            onClick={(e) => {
              e.stopPropagation();
              setShowStepTimes(!showStepTimes);
            }}
            className={classes.durationButton}
          >
            <span className={classes.durationHours}>{`${step.times.from} - ${step.times.to}`}</span>
          </div>
        </CustomPopover>
      );
    }
    return <div />;
  }, [showStepTimes, step.id, step.poi.duration, step.times.from, step.times.to, t, timesClicked]);

  const replaceRemoveButtons = useMemo(
    () => (
      <CustomPopover
        show={showMobileButtons}
        content={
          <div className={classes.mobileButtons} key={`mobile-buttons-${step.id}`} role="button" tabIndex={0} onKeyPress={() => {}} onClick={(e) => e.stopPropagation()}>
            <div
              className={classes.flex}
              onKeyDown={() => {}}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                const randomPoi = alternativePois[Math.floor(Math.random() * alternativePois.length)];
                alternativeReplace(randomPoi);
                setShowMobileButtons(!showMobileButtons);
              }}
            >
              <Replace stroke="#0B1215" />
              <div>Replace</div>
            </div>
            <div
              className={classes.flex}
              onKeyDown={() => {}}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                userReactionRemoveStep();
                setShowMobileButtons(!showMobileButtons);
              }}
            >
              <X2 width="20" height="20" fill="#0B1215" />
              <div>Remove</div>
            </div>
          </div>
        }
        positions={['bottom', 'right']}
        backdropClick={(e) => {
          e.stopPropagation();
          setShowMobileButtons(false);
        }}
      >
        <div
          role="button"
          key={Math.floor(Math.random() * 100)}
          tabIndex={0}
          onKeyPress={() => {}}
          onClick={(e) => {
            e.stopPropagation();
            setShowMobileButtons(!showMobileButtons);
          }}
        >
          <Dots />
        </div>
      </CustomPopover>
    ),
    [alternativePois, alternativeReplace, showMobileButtons, step.id, userReactionRemoveStep],
  );

  const stepCardImageClasses = [classes.stepCardImage];
  if (step.poi.status === false) {
    stepCardImageClasses.push(classes.stepCardBlackWhite);
  }

  let info;
  if (step.poi.status === false) {
    if (step.poi.name.includes('Permanently Closed')) info = <div className={classes.statusWarning}>{t('trips.myTrips.itinerary.step.status.permanentlyClosed')}</div>;
    else info = <div className={classes.statusWarning}>{t('trips.myTrips.itinerary.step.status.temporarilyClosed')}</div>;
  } else if (attraction) {
    info = (
      <>
        {step.poi.description !== null ? (
          <div className={classes.stepInfo}>
            <span className={`${classes.description} my1`}>{step.poi.description}</span>
          </div>
        ) : (
          <div className={classes.stepInfo}>
            <span className={`${classes.description} my1`}>
              <b>{t('trips.myTrips.itinerary.step.address')}: </b>
              {step.poi.address}
            </span>
          </div>
        )}
        {step.poi.rating !== null && (
          <div className={classes.ratingPriceIcons}>
            <div className={classes.flexItemCenter}>
              <div className={classes.rating}>{step.poi.rating}</div>
              <StarIcon className="pb-[1px]" />
              <div className={classes.rating}>({step.poi.ratingCount})</div>
            </div>
          </div>
        )}
      </>
    );
  } else if (notCategorized) {
    info = (
      <>
        {step.poi.description !== null && (
          <div className={classes.stepInfo}>
            <span className={`${classes.description} my1`}>{step.poi.description}</span>
          </div>
        )}
        <div className={classes.stepInfo}>
          <span className={`${classes.description} my1`}>
            <b>{t('trips.myTrips.itinerary.step.address')}: </b>
            {step.poi.address}
          </span>
        </div>
      </>
    );
  } else {
    info = (
      <>
        <div className={classes.stepInfo}>
          {uniqueCuisines.length > 0 && !hideCuisine && (
            <>
              <span className={`${classes.cuisines} mt1`}>
                {/* <b>{t('trips.myTrips.itinerary.step.cuisine')}: </b> */}
                {uniqueCuisines.join(', ')}
              </span>
            </>
          )}
          {/* {step.poi.tags.length > 0 && !hideFeatures && (
            <span className={classes.features}>
              <b>{t('trips.myTrips.itinerary.step.features')}: </b>
              {step.poi.tags.length > 0 ? step.poi.tags.join(', ') : 'empty'}
            </span>
          )} */}
        </div>

        <div className={classes.ratingPriceIcons}>
          {step.poi.rating !== null && (
            <div className={classes.flexItemCenter}>
              <div className={classes.rating}>{step.poi.rating}</div>
              <StarIcon />
              <div className={classes.rating}>({step.poi.ratingCount})</div>
            </div>
          )}
          {step.poi.price ? (
            <div className={classes.flexItemCenter}>
              <span className={`${classes.stepCardBull} px3`}>&bull;</span>
              <Price price={step.poi.price} />
            </div>
          ) : null}
        </div>
      </>
    );
  }

  const bookings = step.poi.bookings || [];
  const hasBooking = (!hideTourTicketIcons && !!step.poi.hasBookings) || (!hideReservationIcon && helper.restaurantReservationAvailable(bookings, RESTAURANT_RESERVATION_PROVIDER_IDS));
  return (
    <div style={{ marginLeft: isWidget ? '1.5rem' : '0' }} className={`${classes.stepCardMain} ${step.warningMessage.length > 0 ? classes.redBorder : ''}`}>
      <div
        className={classes.stepCard}
        onKeyDown={() => {}}
        role="button"
        tabIndex={0}
        onClick={() => {
          clicked(step);
        }}
      >
        <div className={stepCardImageClasses.join(' ')}>
          <ImgLazy src={placeImg} alt={step.poi.name} x={256} y={256} />
          <div className={classes.stepOrder}>{`${step.order + 1}`}</div>
          {step.warningMessage.length > 0 && (
            <div className={classes.tooltip} data-tooltip={step.warningMessage.join(' ')} data-tooltip-position="bottom">
              <div className={`${classes.blob} ${classes.red}`}>
                <Info2 fill="#fff" size="12px" />
              </div>
            </div>
          )}
        </div>
        <div className={classes.stepCardinformation}>
          <div className={classes.stepInfoHeader}>
            {!hideStepsTime ? stepsTime : <div />}
            <div className={classes.stepInfoHeaderButtons}>
              <div className={classes.iconButtonsDesktop}>
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                {(window as any).tconfig?.ELIGIBILITY_TYPE === 'visa' && step.poi.externalUrl && (
                  // eslint-disable-next-line react/button-has-type
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      window.open(step.poi.externalUrl!, '_blank', 'noopener,noreferrer');
                    }}
                    className="inline-flex items-center justify-center ml-1 align-middle"
                    aria-label="External link"
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */}
                    <img src={(window as any).tconfig?.IMAGE_PATHS?.LOGO_PATH_LIGHT} alt="External link" className="w-auto object-contain hover:opacity-80 transition-opacity" style={{ height: '0.8rem' }} />
                  </button>
                )}
                {alternativePois.length > 0 && (
                  <div
                    className={classes.replaceButton}
                    onKeyDown={() => {}}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      const randomPoi = alternativePois[Math.floor(Math.random() * alternativePois.length)];
                      alternativeReplace(randomPoi);
                    }}
                  >
                    <Replace stroke="var(--primary-color)" />
                  </div>
                )}
                <div
                  className={classes.removeButton}
                  onKeyDown={() => {}}
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    userReactionRemoveStep();
                  }}
                >
                  <X fill="var(--primary-color)" />
                </div>
              </div>
              <div className={classes.iconButtonsMobile}>{replaceRemoveButtons}</div>

              {hasBooking && (
                <div
                  className={classes.bookButton + (tourTicketProductsLoading ? ` ${classes.disabled}` : '')}
                  role="button"
                  tabIndex={tourTicketProductsLoading ? -1 : 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tourTicketProductsLoading) return;

                    if (onBookClick) {
                      onBookClick(step);
                    }
                  }}
                  onKeyDown={() => {}}
                >
                  {t('book')}

                  {tourTicketProductsLoading && (
                    <span className={classes.bookButtonLoader}>
                      <TourTicketLazyLoading />
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <h4 className={classes.stepCardName}>{step.poi.name}</h4>
          {info}
          {/*  <div className={classes.poiAbilities}>
            {tourTicketProductsLoading && step.poi.category.some((c) => c.id === 1) ? (
              <div className={classes.loadingIndicator}>
                <TourTicketLazyLoading />
              </div>
            ) : (
              <>
                {helper.tourAvailable(step.poi.bookings, TOUR_PROVIDER_IDS, gygTourIds, bbTourIds, viatorTourIds, toristyTourIds) && !hideTourTicketIcons && (
                  <div className={classes.poiAbilityItem}>
                    <Tour fill="#000" />
                  </div>
                )}
                {helper.ticketAvailable(step.poi.bookings, TICKET_PROVIDER_IDS, gygTourIds, bbTourIds, viatorTourIds, toristyTourIds) && !hideTourTicketIcons && (
                  <div className={classes.poiAbilityItem}>
                    <Ticket fill="#000" />
                  </div>
                )}
                {helper.restaurantReservationAvailable(step.poi.bookings, RESTAURANT_RESERVATION_PROVIDER_IDS) && !hideReservationIcon && (
                  <div className={classes.poiAbilityItem}>
                    <Reservation />
                  </div>
                )}
                {helper.offerAvailable(step.poi.offers) && !hideOfferIcon && (
                  <div className={classes.poiAbilityItem}>
                    <Offer />
                  </div>
                )}
                {step.poi.mustTries.length > 0 && (
                  <div className={classes.poiAbilityItem}>
                    <MustTry />
                  </div>
                )}
              </>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default StepCard;
